import { useState } from "react";
import * as Popover from "@radix-ui/react-popover";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Check, ShieldCheck, Users } from "lucide-react";
import { meQuery, errorMessage } from "@/lib/api";
import { type ProjectData, type Task, type Issue } from "@/lib/store";
import { toast } from "@/lib/toast";
import { Avatar } from "./PropertyIcons";

export type ApprovalConfig = { approval_required: boolean; approver_ids: string[] };
export const NO_APPROVAL: ApprovalConfig = { approval_required: false, approver_ids: [] };

/** Edit the policy as one change, so an enabled policy never has zero reviewers. */
export function ApprovalPicker({ d, value, onChange, disabled = false }: {
  d: ProjectData; value: ApprovalConfig; onChange: (value: ApprovalConfig) => void | Promise<unknown>; disabled?: boolean;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const config = { approval_required: !!value.approval_required, approver_ids: value.approver_ids ?? [] };
  const [draft, setDraft] = useState(config);
  const [query, setQuery] = useState("");
  const [busy, setBusy] = useState(false);
  const members = d.members.filter((m) => `${m.name} ${m.email}`.toLowerCase().includes(query.toLowerCase()));
  const apply = async () => {
    setBusy(true);
    try {
      await onChange({ approval_required: draft.approval_required, approver_ids: draft.approval_required ? draft.approver_ids : [] });
      setOpen(false);
    } catch (e) { toast(errorMessage(e), { tone: "error" }); }
    finally { setBusy(false); }
  };
  return <Popover.Root open={open} onOpenChange={(next) => { if (busy) return; if (next) { setDraft(config); setQuery(""); } setOpen(next); }}>
    <Popover.Trigger asChild>
      <button type="button" disabled={disabled} className={`inline-flex h-7 max-w-full items-center gap-1.5 rounded-md border border-border px-2 text-[12.5px] hover:bg-hover disabled:cursor-default ${value.approval_required ? "text-accent" : "text-muted"}`}>
        <ShieldCheck className="h-3.5 w-3.5 shrink-0" />{t(value.approval_required ? "approval.required" : "approval.none")}
        {value.approval_required && <span className="text-[11px] tabular-nums">{value.approver_ids.length}</span>}
      </button>
    </Popover.Trigger>
    <Popover.Portal>
      <Popover.Content align="start" sideOffset={8} className="z-[70] w-[300px] rounded-xl border border-border bg-elevated p-3 shadow-2xl" aria-label={t("approval.settings")}>
        <label className="flex cursor-pointer items-center justify-between gap-3 text-[13px] font-medium">
          {t("approval.required")}<input type="checkbox" className="h-4 w-4 accent-[var(--accent)]" checked={draft.approval_required} disabled={busy} onChange={(e) => setDraft({ ...draft, approval_required: e.target.checked })} />
        </label>
        {draft.approval_required && <>
          <div className="mb-2 mt-4 text-[12px] text-muted">{t("approval.select")}</div>
          <input className="input mb-2 h-8 w-full text-[12px]" aria-label={t("approval.search")} placeholder={t("approval.search")} value={query} onChange={(e) => setQuery(e.target.value)} />
          <div className="max-h-[224px] space-y-0.5 overflow-y-auto">
            {members.map((m) => <label key={m.user_id} className="flex cursor-pointer items-center gap-2 rounded-md px-1.5 py-2 text-[12.5px] hover:bg-hover">
              <input type="checkbox" className="h-3.5 w-3.5 shrink-0 accent-[var(--accent)]" checked={draft.approver_ids.includes(m.user_id)} disabled={busy} onChange={(e) => setDraft({ ...draft, approver_ids: e.target.checked ? [...draft.approver_ids, m.user_id] : draft.approver_ids.filter((id) => id !== m.user_id) })} />
              <Avatar user={d.users[m.user_id]} size={20} /><span className="min-w-0 truncate">{m.name}<span className="ml-1.5 text-faint">{m.email}</span></span>
            </label>)}
          </div>
          <p className="mt-3 text-[11.5px] leading-5 text-faint">{t("approval.anyOne")}</p>
        </>}
        <div className="mt-3 flex justify-end border-t border-border pt-3"><button type="button" className="btn btn-primary h-7 text-[12px]" disabled={busy || (draft.approval_required && !draft.approver_ids.length)} onClick={() => void apply()}>{t("common.apply")}</button></div>
      </Popover.Content>
    </Popover.Portal>
  </Popover.Root>;
}

export function ApprovalSection({ d, item, status, onChange, onApprove }: {
  d: ProjectData; item: Task | Issue; status: string; onChange: (value: ApprovalConfig) => Promise<unknown>; onApprove: () => Promise<unknown>;
}) {
  const { t, i18n } = useTranslation();
  const me = useQuery(meQuery).data?.user;
  const [busy, setBusy] = useState(false);
  const canReview = !!d.project.role && status === "done" && !!me && (item.approver_ids ?? []).includes(me.id);
  const approve = async () => {
    setBusy(true);
    try { await onApprove(); toast(t("approval.completed")); }
    catch (e) { toast(errorMessage(e), { tone: "error" }); }
    finally { setBusy(false); }
  };
  return <section className="my-4 border-y border-border py-4" aria-label={t("approval.settings")}>
    <div className="mb-2 text-[11.5px] font-medium text-faint">{t("approval.title")}</div>
    <ApprovalPicker d={d} value={item} onChange={onChange} disabled={!d.project.role || busy || status === "completed"} />
    {item.approval_required && <>
      <div className="mt-3 flex flex-wrap gap-1.5">{item.approver_ids.map((id) => <span key={id} className="inline-flex items-center gap-1.5 rounded-md bg-hover px-2 py-1 text-[12px]"><Avatar user={d.users[id]} size={16} />{d.users[id]?.name ?? t("approval.formerMember")}{item.approved_by === id && <Check className="h-3 w-3 text-accent" />}</span>)}</div>
      {status === "done" && <div className="mt-3">
        {canReview ? <button className="btn btn-primary h-8 w-full justify-center text-[12px]" disabled={busy} onClick={() => void approve()}><ShieldCheck className="h-3.5 w-3.5" />{t("approval.approve")}</button> : <p className="flex items-center gap-1.5 text-[12px] text-muted"><Users className="h-3.5 w-3.5" />{t("approval.waiting")}</p>}
      </div>}
      {item.approved_at && <p className="mt-3 text-[11.5px] leading-5 text-muted">{t("approval.receipt", { name: d.users[item.approved_by ?? ""]?.name ?? t("approval.formerMember"), date: new Date(item.approved_at).toLocaleString(i18n.language, { dateStyle: "short", timeStyle: "short" }) })}</p>}
    </>}
  </section>;
}
